// Stencil
import {
  Component,
  Host,
  h,
  Prop,
  State,
  Watch,
  Element,
  getAssetPath
} from "@stencil/core";
// Other Libraries
import { MercuryBundles } from "@genexus/mercury";
import { ComboBoxItemModel } from "@genexus/chameleon-controls-library/dist/types/components/combo-box/types";
// Custom Imports
import { Locale } from "../../common/locale";
import { mapTeamsToComboBoxItemModel } from "./helpers";

const COPYRIGHTS_ILLUSTRATION = getAssetPath(
  `./gx-ide-assets/welcome-page/images/copyrights-illustration.png`
);
const ICON_NEXT = getAssetPath(
  `./gx-ide-assets/welcome-page/images/icon-next.svg`
);
const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/typography",
  "components/button",
  "components/combo-box"
];

@Component({
  tag: "gx-ide-select-user-team",
  styleUrl: "select-user-team.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/select-user-team"]
})
export class GxIdeSelectUserTeam {
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;
  // Select User Team References :
  #userTeamsModel: ComboBoxItemModel[] = [];
  #chSelectTeamEl: HTMLChComboBoxRenderElement;
  #displayTeamsTransitionTime: number = 100;

  @Element() el: HTMLGxIdeSelectUserTeamElement;

  /**
   * Defines if the main slide is visible or not. Used for smooth transitions between slides.
   */
  @State() slideIsVisible: boolean = false;

  /**
   * Defines if the team model is redy to be rendered.
   */
  @State() teamsModelReady: boolean = false;

  /**
   * Callback executed when the user clicks the 'Cancel' button
   */
  @Prop() readonly cancelCallback!: () => Promise<void>;

  /**
   * Callback executed when the user clicks the 'Continue' button
   */
  @Prop() readonly continueCallback!: (teamId: string) => Promise<void>;

  /**
   * Array that contains the user's teams
   */
  @Prop() readonly teams!: TeamData[];
  @Watch("teams")
  teamsChanged(newTeams: TeamData[]) {
    this.slideIsVisible = false;
    // switch step
    setTimeout(() => {
      if (newTeams.length) {
        this.#userTeamsModel = mapTeamsToComboBoxItemModel(newTeams);
        this.teamsModelReady = true;
      } else {
        this.teamsModelReady = false;
      }
    }, this.#displayTeamsTransitionTime);
    // show slide
    setTimeout(() => {
      this.slideIsVisible = true;
    }, this.#displayTeamsTransitionTime * 2);
  }

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.teamsChanged(this.teams);

    this.el.style.setProperty(
      "--display-teams-transtion-time",
      this.#displayTeamsTransitionTime.toString() + "ms"
    );
  }

  componentDidLoad() {
    this.slideIsVisible = true;
  }

  #continueHandler = async () => {
    await this.continueCallback(this.#chSelectTeamEl.value);
  };

  #cancelHandler = async () => {
    await this.cancelCallback();
  };

  render() {
    return (
      <Host>
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <section class="slide">
          <img
            src={COPYRIGHTS_ILLUSTRATION}
            class="slide__illustration"
            alt="" // decorative
          />

          <div
            class={{
              "slide__main": true,
              "slide__main--visible": this.slideIsVisible
            }}
          >
            <div class="slide__main-container">
              {this.teamsModelReady ? (
                [
                  <header class="slide__main-header">
                    <img
                      src={ICON_NEXT}
                      alt="" // decorative
                    />
                    <h2
                      // Split the title into two spans for better styling
                      class="slide__main-title"
                    >
                      <span>{this.#componentLocale.title}</span>
                    </h2>
                  </header>,
                  <div class="slide__select-team-description">
                    <p class="body-semi-bold-l">
                      {this.#componentLocale.infoMessage}
                    </p>
                  </div>,
                  <div class="field-group">
                    <div class="field field-block">
                      <label htmlFor="teams">
                        {this.#componentLocale.teams}
                      </label>
                      <ch-combo-box-render
                        id="teams"
                        accessibleName="Teams"
                        class="combo-box teams-list"
                        model={this.#userTeamsModel}
                        value={
                          this.#userTeamsModel && this.#userTeamsModel[0]?.value
                        }
                        ref={(el: HTMLChComboBoxRenderElement) =>
                          (this.#chSelectTeamEl =
                            el as HTMLChComboBoxRenderElement)
                        }
                      ></ch-combo-box-render>
                    </div>
                  </div>,
                  <div class="buttons-container">
                    <button
                      class="button-primary continue-btn"
                      onClick={this.#continueHandler}
                    >
                      {this.#componentLocale.continueButtonCaption}
                    </button>
                    <button
                      class="button-secondary cancel-btn"
                      onClick={this.#cancelHandler}
                    >
                      {this.#componentLocale.cancelButtonCaption}
                    </button>
                  </div>
                ]
              ) : (
                <gx-ide-loader
                  show
                  loaderTitle={this.#componentLocale.loader.title}
                  description={this.#componentLocale.loader.description}
                ></gx-ide-loader>
              )}
            </div>
          </div>
        </section>
      </Host>
    );
  }
}

export type TeamData = {
  id: string;
  name: string;
};
